import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, UserCheck, UserX, Pencil, KeyRound, Stethoscope, CalendarClock, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  "hospital_admin", "branch_admin", "receptionist", "doctor", "optometrist", "nurse",
  "ot_staff", "anesthetist", "lab_staff", "pathologist", "pharmacist", "billing",
  "accountant", "tpa_desk", "management_viewer", "it_admin", "abdm_operator",
];

const DAY_OPTIONS = [
  { v: 0, l: "Mon" }, { v: 1, l: "Tue" }, { v: 2, l: "Wed" }, { v: 3, l: "Thu" },
  { v: 4, l: "Fri" }, { v: 5, l: "Sat" }, { v: 6, l: "Sun" },
];

const EMPTY = { name: "", email: "", password: "", role: "doctor", branch_id: "", branch_ids: [], branch_pin: "", phone: "", qualification: "", registration_no: "", opd_rate: 0, opd_rate_by_category: {}, slot_minutes: 15, availability: [], module_access: [] };

export default function AdminUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [modulesCatalog, setModulesCatalog] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY);

  const [panels, setPanels] = useState([]);

  const load = async () => {
    const [{ data: u }, { data: b }, { data: m }, { data: pc }] = await Promise.all([
      api.get("/hospital/users"),
      api.get("/hospital/branches"),
      api.get("/hospital/modules-catalog"),
      api.get("/panel/companies").catch(() => ({ data: { items: [] } })),
    ]);
    setItems(u.items); setBranches(b.items);
    setModulesCatalog(m.modules || []);
    setRoleDefaults(m.role_defaults || {});
    setPanels((pc.items || []).filter((p) => p.status !== "inactive"));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const toggleBranchId = (bid) => setForm(s => ({
    ...s,
    branch_ids: s.branch_ids.includes(bid) ? s.branch_ids.filter(x => x !== bid) : [...s.branch_ids, bid],
  }));
  const toggleModule = (mkey) => setForm((s) => ({
    ...s,
    module_access: s.module_access.includes(mkey)
      ? s.module_access.filter((x) => x !== mkey)
      : [...s.module_access, mkey],
  }));
  // When the role changes, pre-fill module_access with that role's default
  // (only if the user hasn't explicitly customized it for this session yet).
  const onRoleChange = (newRole) => {
    setForm((s) => ({
      ...s,
      role: newRole,
      module_access: roleDefaults[newRole] ? [...roleDefaults[newRole]] : [],
    }));
  };

  const openCreate = () => {
    setEditing(null);
    // Initial form: default role's module access pre-checked
    const defaultRole = "doctor";
    setForm({ ...EMPTY, role: defaultRole, module_access: roleDefaults[defaultRole] ? [...roleDefaults[defaultRole]] : [] });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "", email: u.email || "", password: "", role: u.role || "doctor",
      branch_id: u.branch_id || "", branch_ids: u.branch_ids || [],
      branch_pin: "", // never preload PIN — admin types it fresh if they want to reset
      phone: u.phone || "",
      qualification: u.qualification || "", registration_no: u.registration_no || "",
      opd_rate: u.opd_rate || 0,
      opd_rate_by_category: u.opd_rate_by_category || {},
      slot_minutes: u.slot_minutes || 15,
      availability: u.availability || [],
      // If user has explicit override use it; else pre-tick role defaults.
      module_access: (u.module_access && u.module_access.length)
        ? [...u.module_access]
        : (roleDefaults[u.role || "doctor"] ? [...roleDefaults[u.role || "doctor"]] : []),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      // Compare current form.module_access against role defaults — if user simply
      // accepted defaults, send empty array so backend stays in "use role default" mode.
      const roleDef = roleDefaults[form.role] || [];
      const sameAsDefault = roleDef.length === form.module_access.length && roleDef.every((m) => form.module_access.includes(m));
      const moduleAccessPayload = sameAsDefault ? [] : form.module_access;

      if (editing) {
        // PATCH only the fields that have meaningful values
        const patch = {
          name: form.name, phone: form.phone, role: form.role,
          branch_id: form.branch_id || null,
          branch_ids: form.branch_ids,
          qualification: form.qualification, registration_no: form.registration_no,
          module_access: moduleAccessPayload,
        };
        if (form.password) patch.password = form.password;
        if (form.branch_pin) patch.branch_pin = form.branch_pin;
        if (form.role === "doctor") {
          patch.opd_rate = Number(form.opd_rate) || 0;
          patch.opd_rate_by_category = form.opd_rate_by_category || {};
          patch.slot_minutes = Number(form.slot_minutes) || 15;
          patch.availability = form.availability;
        }
        await api.patch(`/hospital/users/${editing.id}`, patch);
        toast.success("User updated");
      } else {
        await api.post("/hospital/users", { ...form, module_access: moduleAccessPayload });
        toast.success("User created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const addAvailability = () => {
    const default_branch = form.branch_id || form.branch_ids?.[0] || branches[0]?.id || "";
    setForm(s => ({ ...s, availability: [...s.availability, { branch_id: default_branch, day_of_week: 0, start_time: "09:00", end_time: "13:00" }] }));
  };

  // ---- Per-category / per-panel OPD rate matrix ----
  const setCashRate = (v) => setForm((s) => ({ ...s, opd_rate_by_category: { ...(s.opd_rate_by_category || {}), cash: Number(v) || 0 } }));
  const setFreeRate = (v) => setForm((s) => ({ ...s, opd_rate_by_category: { ...(s.opd_rate_by_category || {}), free: Number(v) || 0 } }));
  const setPanelRate = (panelId, v) => setForm((s) => ({
    ...s,
    opd_rate_by_category: {
      ...(s.opd_rate_by_category || {}),
      panel: { ...((s.opd_rate_by_category || {}).panel || {}), [panelId]: Number(v) || 0 },
    },
  }));
  const updateAvailability = (idx, key, value) => {
    setForm(s => ({ ...s, availability: s.availability.map((row, i) => i === idx ? { ...row, [key]: value } : row) }));
  };
  const removeAvailability = (idx) => {
    setForm(s => ({ ...s, availability: s.availability.filter((_, i) => i !== idx) }));
  };

  const doctorBranches = () => {
    const ids = [...new Set([form.branch_id, ...(form.branch_ids || [])].filter(Boolean))];
    return branches.filter(b => ids.includes(b.id));
  };
  const toggleStatus = async (u) => {
    const ns = u.status === "active" ? "inactive" : "active";
    await api.patch(`/hospital/users/${u.id}/status`, { status: ns });
    load();
  };

  const branchNameById = (id) => branches.find(b => b.id === id)?.name || "—";

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-label">Administration</div>
          <h1 className="font-heading text-3xl font-semibold">Users & Roles</h1>
          <p className="mt-1 text-sm text-ink-muted">{items.length} user{items.length === 1 ? "" : "s"}</p>
        </div>
        <Button onClick={openCreate} className="bg-brand hover:bg-brand-700" data-testid="users-new-btn"><Plus size={14} className="mr-2" />Add user</Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-left">Branches</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map(u => {
              const allBranches = [u.branch_id, ...(u.branch_ids || [])].filter(Boolean);
              return (
                <tr key={u.id} className="hover:bg-secondary/30" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-2 font-medium">{u.name}</td>
                  <td className="px-4 py-2">{u.email}</td>
                  <td className="px-4 py-2 capitalize">{u.role.replace(/_/g, " ")}</td>
                  <td className="px-4 py-2 text-xs text-ink-muted">
                    {allBranches.length === 0 ? "Hospital-wide" : allBranches.map(branchNameById).join(", ")}
                  </td>
                  <td className="px-4 py-2 capitalize">{u.status}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`user-edit-${u.email}`}><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleStatus(u)} data-testid={`user-toggle-${u.email}`}>
                        {u.status === "active" ? <UserX size={14} className="text-rose-500" /> : <UserCheck size={14} className="text-leaf" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 max-h-[70vh] overflow-y-auto pr-1">
            <div><Label>Name</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="user-name-input" /></div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={e => set("email", e.target.value)} data-testid="user-email-input" />
              {editing && <div className="mt-1 text-[11px] text-ink-muted">Email can't be changed after creation.</div>}
            </div>
            <div>
              <Label>{editing ? "New password (leave blank to keep)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={e => set("password", e.target.value)} data-testid="user-pwd-input" placeholder={editing ? "Leave blank to keep current" : ""} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={onRoleChange}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(user.role === "super_admin" ? ["super_admin", ...ROLES] : ROLES).map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Primary branch</Label>
              <Select value={form.branch_id || "__none__"} onValueChange={(v) => set("branch_id", v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="No branch (hospital-wide)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No primary branch (hospital-wide)</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name} · {b.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Additional branches (multi-branch access)</Label>
              <div className="mt-2 grid gap-2 rounded-md border border-border p-3">
                {branches.length === 0 && <div className="text-xs text-ink-muted">No branches available.</div>}
                {branches.filter(b => b.id !== form.branch_id).map(b => (
                  <label key={b.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.branch_ids.includes(b.id)}
                      onCheckedChange={() => toggleBranchId(b.id)}
                      data-testid={`user-branchid-${b.code}`}
                    />
                    <span>{b.name}</span>
                    <span className="text-xs text-ink-muted">· {b.code}</span>
                  </label>
                ))}
                {branches.length > 0 && branches.filter(b => b.id !== form.branch_id).length === 0 && (
                  <div className="text-xs text-ink-muted">All branches are already covered by the primary branch.</div>
                )}
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-2"><KeyRound size={12} />{editing ? "Reset Branch PIN (leave blank to keep)" : "Branch PIN (optional)"}</Label>
              <Input
                type="password" inputMode="numeric" value={form.branch_pin}
                onChange={e => set("branch_pin", e.target.value)}
                placeholder={editing ? "Leave blank to keep current PIN" : "Required at branch selection"}
                data-testid="user-branch-pin-input"
              />
              <div className="mt-1 text-[11px] text-ink-muted">If set, the user must enter this PIN at the branch-selection screen.</div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Module access</Label>
                <div className="flex gap-2 text-[10px]">
                  <button
                    type="button"
                    className="font-medium text-brand hover:underline disabled:text-ink-muted disabled:no-underline"
                    onClick={() => set("module_access", roleDefaults[form.role] ? [...roleDefaults[form.role]] : [])}
                    data-testid="user-modules-reset-defaults"
                  >
                    Reset to {form.role.replace(/_/g, " ")} defaults
                  </button>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5 rounded-md border border-border p-3 sm:grid-cols-3">
                {modulesCatalog.map((m) => {
                  const isInRoleDefault = (roleDefaults[form.role] || []).includes(m.key);
                  if (!isInRoleDefault) return null; // can't grant beyond role defaults
                  const checked = form.module_access.includes(m.key);
                  return (
                    <label
                      key={m.key}
                      className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-[12px] transition ${
                        checked ? "border-brand/40 bg-brand/5" : "border-border bg-white hover:border-brand/30"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleModule(m.key)}
                        data-testid={`user-module-${m.key}`}
                      />
                      <span className="leading-tight">{m.label}</span>
                    </label>
                  );
                })}
                {(roleDefaults[form.role] || []).length === 0 && (
                  <div className="col-span-full text-xs text-ink-muted">This role has no module-level access (it operates outside the EMR).</div>
                )}
              </div>
              <div className="mt-1 text-[11px] text-ink-muted">
                Tick the modules this user should see in their sidebar. Unticking restricts access for this user only — role defaults remain unchanged.
              </div>
            </div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} /></div>

            {form.role === "doctor" && (
              <div className="rounded-md border border-brand/30 bg-brand/5 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand"><Stethoscope size={14} />Doctor settings</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Qualification</Label>
                    <Input value={form.qualification} onChange={e => set("qualification", e.target.value)} placeholder="e.g. MBBS, MS Ophth" data-testid="user-qualification" />
                  </div>
                  <div>
                    <Label>Registration / Council no.</Label>
                    <Input value={form.registration_no} onChange={e => set("registration_no", e.target.value)} data-testid="user-regno" />
                  </div>
                  <div>
                    <Label>OPD consultation rate (₹) · Cash default</Label>
                    <Input type="number" min="0" value={form.opd_rate} onChange={e => set("opd_rate", e.target.value)} data-testid="user-opd-rate" />
                    <div className="mt-1 text-[10px] text-ink-muted">Fallback used when no category-specific rate below is set.</div>
                  </div>
                  <div>
                    <Label>Slot length (minutes)</Label>
                    <Select value={String(form.slot_minutes)} onValueChange={(v) => set("slot_minutes", Number(v))}>
                      <SelectTrigger data-testid="user-slot-minutes"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[5, 10, 15, 20, 30, 45, 60].map(n => <SelectItem key={n} value={String(n)}>{n} minutes</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="mb-1.5 flex items-center justify-between">
                    <Label className="flex items-center gap-1.5"><CalendarClock size={12} />Branch-wise availability</Label>
                    <Button size="sm" variant="outline" onClick={addAvailability} disabled={doctorBranches().length === 0} data-testid="user-add-availability"><Plus size={12} className="mr-1" />Add window</Button>
                  </div>
                  {doctorBranches().length === 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700">Pick at least one branch above so the doctor can have a schedule.</div>
                  )}
                  {form.availability.length === 0 && doctorBranches().length > 0 && (
                    <div className="rounded-md border border-dashed border-border p-3 text-center text-[11px] text-ink-muted">No availability windows yet — click <b>Add window</b>.</div>
                  )}
                  <div className="space-y-2">
                    {form.availability.map((row, i) => (
                      <div key={i} className="grid grid-cols-12 items-center gap-2 rounded-md border border-border bg-white p-2 text-xs" data-testid={`user-availability-row-${i}`}>
                        <div className="col-span-4">
                          <Select value={row.branch_id} onValueChange={(v) => updateAvailability(i, "branch_id", v)}>
                            <SelectTrigger data-testid={`user-availability-branch-${i}`}><SelectValue placeholder="Branch" /></SelectTrigger>
                            <SelectContent>{doctorBranches().map(b => <SelectItem key={b.id} value={b.id}>{b.name} · {b.code}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Select value={String(row.day_of_week)} onValueChange={(v) => updateAvailability(i, "day_of_week", Number(v))}>
                            <SelectTrigger data-testid={`user-availability-day-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{DAY_OPTIONS.map(d => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Input type="time" value={row.start_time} onChange={(e) => updateAvailability(i, "start_time", e.target.value)} data-testid={`user-availability-start-${i}`} />
                        </div>
                        <div className="col-span-2">
                          <Input type="time" value={row.end_time} onChange={(e) => updateAvailability(i, "end_time", e.target.value)} data-testid={`user-availability-end-${i}`} />
                        </div>
                        <div className="col-span-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => removeAvailability(i)} data-testid={`user-availability-remove-${i}`}><Trash2 size={12} className="text-rose-500" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 rounded-md border border-border bg-white p-3" data-testid="opd-rate-matrix">
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-brand">
                    <Stethoscope size={12} />OPD rate by category / panel
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1.5">
                      <span className="text-xs">Cash</span>
                      <Input type="number" min="0" className="h-7 w-24 text-right text-xs"
                        value={form.opd_rate_by_category?.cash ?? ""}
                        placeholder={String(form.opd_rate || 0)}
                        onChange={(e) => setCashRate(e.target.value)}
                        data-testid="opd-rate-cash" />
                    </div>
                    <div className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1.5">
                      <span className="text-xs">Free</span>
                      <Input type="number" min="0" className="h-7 w-24 text-right text-xs"
                        value={form.opd_rate_by_category?.free ?? 0}
                        onChange={(e) => setFreeRate(e.target.value)}
                        data-testid="opd-rate-free" />
                    </div>
                    {panels.length === 0 && (
                      <div className="col-span-full rounded-md border border-dashed border-border p-2 text-center text-[11px] text-ink-muted">
                        No active panels yet — add them under <b>Panel &amp; insurance</b> to set per-panel rates.
                      </div>
                    )}
                    {panels.map((p) => (
                      <div key={p.id} className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1.5">
                        <span className="text-xs" title={p.code || ""}>{p.name}</span>
                        <Input type="number" min="0" className="h-7 w-24 text-right text-xs"
                          value={form.opd_rate_by_category?.panel?.[p.id] ?? ""}
                          placeholder={String(form.opd_rate || 0)}
                          onChange={(e) => setPanelRate(p.id, e.target.value)}
                          data-testid={`opd-rate-panel-${p.id}`} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-1.5 text-[10px] text-ink-muted">
                    Leave a row blank to fall back to the Cash default above. Rate is applied automatically at patient registration.
                  </div>
                </div>
              </div>
            )}

            <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="user-save-btn">{editing ? "Save changes" : "Create user"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
